import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { isTouchLike } from './pointerType';
import './Tooltip.css';

const OPEN_DELAY_MS = 300;
const SKIP_DELAY_MS = 150;

const ProviderPresent = createContext(false);

/**
 * Baustein — Tooltip.
 * Kurzer Erklärtext an einem Bedienelement; ersetzt das native `title`-Attribut, das träge
 * erscheint, nach einem Klick verschwindet und per Tastatur nicht erreichbar ist.
 *
 *   <Tooltip label="Friedhof ansehen">
 *     <button type="button" onClick={open}><Skull size={14} aria-hidden />3</button>
 *   </Tooltip>
 *
 * Props:
 *   label    — der Text; steht zugleich als `aria-label` am Auslöser, sofern der kein eigenes
 *              trägt (ein eigenes `aria-label` gewinnt, etwa wenn es zusätzlich eine Zahl nennt).
 *   side     — 'top' | 'right' | 'bottom' | 'left' (default 'top'); Radix weicht bei Platzmangel aus.
 *   children — genau ein Element, das Ref und Props an sein DOM-Element weiterreicht (Radix
 *              `asChild`). Es muss fokussierbar sein — ein `<span>` braucht `tabIndex={0}` —,
 *              sonst erreicht die Tastatur den Text nicht. Eigene Handler des Kindes laufen vor
 *              denen des Tooltips; ein `stopPropagation` darin wirkt weiter. Der Tooltip reicht
 *              `onClick` ans Kind: ein Baustein, der daran seine Gestalt festmacht (`ui/Badge` wird
 *              mit `onClick` zum Knopf), bekommt deshalb einen fokussierbaren `<span>` als Hülle.
 *   Weitere Props und die `ref` gehen an den Auslöser: ein Tooltip taugt damit selbst als Kind
 *   eines Radix-`Trigger asChild` (Popover, Dropdown). Deren `data-state` gewinnt dabei über das
 *   des Tooltips — Stile wie `[data-state='open']` am Auslöser meinen die Fläche, nicht den Text.
 *
 * Verhalten:
 *   - Maus: erscheint nach 300 ms Verweilen; zwischen benachbarten Auslösern wechselt er
 *     150 ms lang ohne neue Wartezeit. Ein Klick lässt ihn offen; er schließt beim Verlassen
 *     des Auslösers, bei Escape, beim Fokusverlust und bei einem Druck außerhalb.
 *   - Tastatur: erscheint sofort, wenn der Auslöser den Fokus bekommt.
 *   - Finger und Stift (`pointerType` ≠ mouse): erscheint nie. Ein Tipp gibt dem Auslöser in
 *     Chrome den Fokus — der Text käme sonst zu jedem Tipp mit, und beim Langdruck zusätzlich zum
 *     Sheet, das der Langdruck öffnet. Nach dem Fokusverlust gilt wieder das Tastatur-Verhalten.
 *   - Inhalt im Portal (über Menüs und Sheets), Tokens `--bg-elevated`/`--text-primary`.
 *
 * `TooltipProvider` (einmal in `App.js`) setzt Verzögerung und Wechselzeit für die ganze App.
 * Fehlt er — Tests, isolierte Renderings —, bringt jeder Tooltip einen eigenen mit denselben
 * Werten mit; nur der Wechsel ohne Wartezeit gilt dann nicht über Tooltip-Grenzen hinweg.
 */
const Tooltip = React.forwardRef(function Tooltip({ label, side = 'top', children, ...rest }, forwardedRef) {
  const hasProvider = useContext(ProviderPresent);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const setTriggerRef = useCallback((node) => {
    triggerRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);
  const keepOpenRef = useRef(false);
  const touchRef = useRef(false);

  // Radix schließt bei jedem Druck und Klick auf den Auslöser; diese Schließ-Anfrage kommt
  // synchron im selben Handler und wird verworfen. Der Merker verfällt danach, auch wenn ein
  // Handler des Kindes Radix per preventDefault übersprungen hat.
  const keepOpenThroughPress = () => {
    keepOpenRef.current = open && !touchRef.current;
    queueMicrotask(() => { keepOpenRef.current = false; });
  };

  const handleOpenChange = (next) => {
    if (next && touchRef.current) return;
    if (!next && keepOpenRef.current) {
      keepOpenRef.current = false;
      return;
    }
    setOpen(next);
  };

  const child = React.Children.only(children);
  const labelProps = child.props['aria-label'] == null ? { 'aria-label': label } : {};

  const root = (
    <RadixTooltip.Root open={open} onOpenChange={handleOpenChange}>
      <RadixTooltip.Trigger
        asChild
        {...labelProps}
        {...rest}
        ref={setTriggerRef}
        onPointerDown={(e) => {
          rest.onPointerDown?.(e);
          touchRef.current = isTouchLike(e);
          keepOpenThroughPress();
        }}
        onPointerMove={(e) => {
          rest.onPointerMove?.(e);
          if (e.pointerType === 'mouse') touchRef.current = false;
        }}
        onClick={(e) => {
          rest.onClick?.(e);
          keepOpenThroughPress();
        }}
        onBlur={(e) => {
          rest.onBlur?.(e);
          touchRef.current = false;
        }}
      >
        {child}
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          className="ui-tooltip"
          side={side}
          sideOffset={6}
          collisionPadding={8}
          onPointerDownOutside={(e) => {
            if (triggerRef.current?.contains(e.target)) e.preventDefault();
          }}
        >
          {label}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );

  if (hasProvider) return root;
  return (
    <RadixTooltip.Provider delayDuration={OPEN_DELAY_MS} skipDelayDuration={SKIP_DELAY_MS}>
      {root}
    </RadixTooltip.Provider>
  );
});

export default Tooltip;

/** App-weiter Rahmen: 300 ms bis zum Erscheinen, 150 ms Wechsel ohne neue Wartezeit. */
export function TooltipProvider({ children }) {
  return (
    <RadixTooltip.Provider delayDuration={OPEN_DELAY_MS} skipDelayDuration={SKIP_DELAY_MS}>
      <ProviderPresent.Provider value>{children}</ProviderPresent.Provider>
    </RadixTooltip.Provider>
  );
}
